import type {AtsEntity} from 'src/types/ats-entities';
import type {
  ATSListFilterQuery,
  ATSListFilterResponse,
} from 'src/types/audience-ats-list';
import type {Sort, ListFilterPayload} from 'src/types/audience-csv-list';
import type {Dispatch, ThunkAction} from 'src/reducers';
import * as reduxApiBff from 'src/utils/redux-api-bff';


export const RECEIVE_ATS_LISTS = 'audience-ats-list/receive-ats-lists';
export const RECEIVE_ATS_ENTITIES = 'audience-ats-list/receive-ats-entities';
export const RECEIVE_ATS_LIST_SORT = 'audience-ats-list/receive-ats-list-sort';
export const RECEIVE_ATS_LIST_QUERY =
  'audience-ats-list/receive-ats-list-query';
export const RECEIVE_ATS_LIST_FILTER =
  'audience-ats-list/receive-ats-list-filter';
export const RECEIVE_ATS_LIST_REFETCH =
  'audience-ats-list/receive-ats-list-refetch';
export const RECEIVE_ATS_LIST_PAGE_NUMBER =
  'audience-ats-list/receive-ats-list-page-number';

type ReceiveATSListsAction = {
  type: 'audience-ats-list/receive-ats-lists';
  payload: ATSListFilterResponse;
};

type ReceiveATSEntitiesAction = {
  type: 'audience-ats-list/receive-ats-entities';
  payload: Array<AtsEntity>;
};

type ReceiveATSListSortAction = {
  type: 'audience-ats-list/receive-ats-list-sort';
  payload: Sort;
};

type ReceiveATSListQueryAction = {
  type: 'audience-ats-list/receive-ats-list-query';
  payload: string;
};

type ReceiveATSListFilterAction = {
  type: 'audience-ats-list/receive-ats-list-filter';
  payload: ListFilterPayload;
};

type ReceiveATSListRefetchAction = {
  type: 'audience-ats-list/receive-ats-list-refetch';
};

type ReceiveATSListPageNumberAction = {
  type: 'audience-ats-list/receive-ats-list-page-number';
  payload: number;
};

export type AudienceATSListActions =
  | ReceiveATSListsAction
  | ReceiveATSEntitiesAction
  | ReceiveATSListSortAction
  | ReceiveATSListQueryAction
  | ReceiveATSListFilterAction
  | ReceiveATSListRefetchAction
  | ReceiveATSListPageNumberAction;

export const receiveATSLists = (
  payload: ATSListFilterResponse,
): ReceiveATSListsAction => ({
  type: RECEIVE_ATS_LISTS,
  payload,
});

export const receiveATSEntities = (
  payload: Array<AtsEntity>,
): ReceiveATSEntitiesAction => ({
  type: RECEIVE_ATS_ENTITIES,
  payload,
});

export const receiveATSListSort = (
  payload: Sort,
): ReceiveATSListSortAction => ({
  type: RECEIVE_ATS_LIST_SORT,
  payload,
});

export const receiveATSListQuery = (
  payload: string,
): ReceiveATSListQueryAction => ({
  type: RECEIVE_ATS_LIST_QUERY,
  payload,
});

export const receiveATSListFilter = (
  payload: ListFilterPayload,
): ReceiveATSListFilterAction => ({
  type: RECEIVE_ATS_LIST_FILTER,
  payload,
});

export const refetchATSList = (): ReceiveATSListRefetchAction => ({
  type: RECEIVE_ATS_LIST_REFETCH,
});

export const receiveATSListPageNumber = (
  payload: number,
): ReceiveATSListPageNumberAction => ({
  type: RECEIVE_ATS_LIST_PAGE_NUMBER,
  payload,
});

export const getATSLists: (
  query: ATSListFilterQuery,
  isNew?: boolean,
) => ThunkAction<unknown> =
  (query: ATSListFilterQuery, isNew: boolean = false) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(reduxApiBff.get('filter/search', query));
      const {filters, totalCount, totalPages} = response;
      dispatch(
        receiveATSLists({
          isNew,
          lists: filters || [],
          pageNo: query.pageNo + 1,
          totalLists: totalCount || 0,
          totalPages: totalPages || 0,
        }),
      );
    } catch (error: any) {
      dispatch(
        receiveATSLists({
          isNew: true,
          lists: [],
          pageNo: 1,
          totalLists: 0,
          totalPages: 1,
        }),
      );
      console.error(error);
    }
  };

export const getATSEntities: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApiBff.get('dobby/tearsheets/target-entity-types'),
      );
      dispatch(receiveATSEntities(response));
    } catch (error: any) {
      dispatch(receiveATSEntities([]));
      console.error(error);
    }
  };

export const createATSLists: (idList: Array<string>) => ThunkAction<unknown> =
  (idList: Array<string>) => async (dispatch: Dispatch) => {
    try {
      await dispatch(reduxApiBff.post('dobby/tearsheets/sync', {idList}));
    } catch (error: any) {
      console.error(error);
      throw new Error('Tearsheets sync error.');
    }
  };

export const deleteATSLists: (listIds: Array<number>) => ThunkAction<unknown> =
  (listIds: Array<number>) => (dispatch: Dispatch) =>
    dispatch(reduxApiBff.del('dobby/tearsheets', {listIds}));
